import { useState, useEffect } from 'react';
import axios from 'axios';
import Storefront     from './components/storefront/Storefront';
import StoneDetailPage from './components/detail/StoneDetailPage';
import CheckoutPage   from './components/checkout/CheckoutPage';
import PaymentResultPage from './components/checkout/PaymentResultPage';
import TrackingPage from './components/tracking/TrackingPage';
import AdminLogin     from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import { HowItWorksPage, TermsPolicyPage } from './components/info/InfoPages';

const TWEAK_DEFAULTS = {
  gridColumns:      3,
  cardStyle:        'classic',
  accentColor:      '#D4AF37',
  showHeroSubtitle: true,
};

function navigate(path) {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new Event('popstate'));
  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
}

function itemIdFromPath(path) {
  const match = String(path || '').match(/^\/item\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : '';
}

export default function App() {
  const initialAdminToken = localStorage.getItem('kingstradeAdminToken');
  if (initialAdminToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${initialAdminToken}`;
  }

  const [path, setPath]              = useState(window.location.pathname);
  const [adminAuthed, setAdminAuthed] = useState(Boolean(initialAdminToken));
  const [adminUser, setAdminUser]     = useState(null);
  const [selectedStone, setSelected]  = useState(null);
  const [detailStone, setDetailStone] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [returnPath, setReturnPath]   = useState('/collection');
  const [checkout, setCheckout]      = useState(false);
  const [tweaks, setTweaks]          = useState(TWEAK_DEFAULTS);
  const [showTweaks, setShowTweaks]  = useState(false);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('kingstradeAdminToken');
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      axios.get('/api/admin/auth/me')
        .then(res => {
          setAdminAuthed(true);
          setAdminUser(res.data.admin);
        })
        .catch(() => {
          localStorage.removeItem('kingstradeAdminToken');
          delete axios.defaults.headers.common.Authorization;
          setAdminAuthed(false);
          setAdminUser(null);
        });
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === '__activate_edit_mode')   setShowTweaks(true);
      if (e.data?.type === '__deactivate_edit_mode') setShowTweaks(false);
    };
    window.addEventListener('message', handler);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    const itemId = itemIdFromPath(path);
    if (!itemId) {
      setDetailStone(null);
      setDetailError('');
      setDetailLoading(false);
      return;
    }

    if (selectedStone?.id === itemId) {
      setDetailStone(selectedStone);
      setDetailError('');
      setDetailLoading(false);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    axios.get(`/api/stones/${encodeURIComponent(itemId)}`)
      .then(res => {
        if (!cancelled) setDetailStone(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          setDetailStone(null);
          setDetailError('Treasure not found.');
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => { cancelled = true; };
  }, [path, selectedStone]);

  // ── Navigation helpers ───────────────────────────────────────────────────
  const goToDetail   = (stone) => {
    setReturnPath(window.location.pathname + window.location.search);
    setSelected(stone);
    setDetailStone(stone);
    setCheckout(false);
    navigate(`/item/${encodeURIComponent(stone.id)}`);
  };
  const goToCheckout = ()      => setCheckout(true);
  const goBack       = ()      => { setSelected(null); setCheckout(false); navigate(returnPath || '/collection'); };

  // ── Admin route ─────────────────────────────────────────────────────────
  if (path === '/admin') {
    if (!adminAuthed) {
      return (
        <AdminLogin
          onLogin={({ token, admin }) => {
            localStorage.setItem('kingstradeAdminToken', token);
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            setAdminUser(admin);
            setAdminAuthed(true);
          }}
          onBack={() => navigate('/')}
        />
      );
    }
    return (
      <AdminDashboard
        admin={adminUser}
        onLogout={() => {
          localStorage.removeItem('kingstradeAdminToken');
          delete axios.defaults.headers.common.Authorization;
          setAdminAuthed(false);
          setAdminUser(null);
          navigate('/');
        }}
      />
    );
  }

  // ── Storefront route ─────────────────────────────────────────────────────
  if (path === '/payment-result') {
    return <PaymentResultPage onBack={() => navigate('/')} />;
  }

  if (path === '/track') {
    return <TrackingPage onBack={() => navigate('/')} />;
  }

  if (path === '/how-it-works') {
    return <HowItWorksPage onBack={() => navigate('/')} />;
  }

  if (path === '/terms-and-policy') {
    return <TermsPolicyPage onBack={() => navigate('/')} />;
  }

  const itemId = itemIdFromPath(path);
  const activeDetailStone = detailStone || selectedStone;

  if (itemId && detailLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: 11, letterSpacing: '0.22em', color: '#8A97A8', fontWeight: 500 }}>LOADING TREASURE...</p>
      </div>
    );
  }

  if (itemId && detailError) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ maxWidth: 420, textAlign: 'center', background: '#fff', border: '1px solid #E8EAED', padding: 40 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.18em', color: '#D4AF37', fontWeight: 700, marginBottom: 16 }}>TREASURE RECORD</p>
          <h1 style={{ fontFamily: 'Playfair Display', fontSize: 34, fontWeight: 400, color: '#0A192F', marginBottom: 14 }}>{detailError}</h1>
          <button onClick={() => navigate('/collection')} style={{ padding: '13px 28px', background: '#D4AF37', border: 'none', cursor: 'pointer', fontSize: 11, letterSpacing: '0.16em', fontWeight: 700, color: '#0F172A' }}>
            BACK TO COLLECTION
          </button>
        </div>
      </div>
    );
  }

  if (itemId && activeDetailStone && !checkout) {
    return (
      <StoneDetailPage
        stone={activeDetailStone}
        onReserve={goToCheckout}
        onBack={goBack}
      />
    );
  }

  if (activeDetailStone && checkout) {
    return (
      <CheckoutPage
        stone={activeDetailStone}
        onClose={goBack}
      />
    );
  }

  return (
    <Storefront
      onBuy={goToDetail}
      mode={path === '/collection' ? 'collection' : 'landing'}
      tweaks={tweaks}
    />
  );
}
